/**
 * Post Mode System
 * 
 * Defines the different modes a post (List or Itinerary) can be in and provides type-safe
 * metadata for each mode. This replaces boolean flags with a clear enum system.
 * 
 * Used by both Lists and Itineraries.
 */

// Use Record<string, unknown> instead of Post class to avoid circular dependencies
// Post class can be large and we only need the structure
export type PostLike = Record<string, unknown> & {
  _id?: string;
  name?: string;
  body?: string;
  post_type?: string;
  is_draft?: boolean;
  is_private?: boolean;
  [key: string]: unknown;
};

/**
 * Enumeration of post modes (applies to both Lists and Itineraries)
 */
export enum PostMode {
  /** Creating new post from scratch */
  NEW = 'new',
  /** Editing own existing post */
  EDIT = 'edit',
  /** Remixing someone else's post (creates new published post) */
  REMIX = 'remix',
  /** Remixing with draft support (creates/updates draft) */
  REMIX_DRAFT = 'remix_draft',
}

export type PostModeType = 
  | PostMode.NEW 
  | PostMode.EDIT 
  | PostMode.REMIX 
  | PostMode.REMIX_DRAFT;

/**
 * Base metadata shared across all modes
 */
export interface BaseModeMetadata {
  mode: PostMode;
  sourcePostId: string | null;      // Original post being edited/remixed
  sourcePostData: PostLike | null;   // Full source post data
}

/**
 * Edit mode metadata
 * User is editing their own existing post
 */
export interface EditModeMetadata extends BaseModeMetadata {
  mode: PostMode.EDIT;
  targetPostId: string;              // Required: Post being updated
  isDraft: boolean;                   // Whether target is a draft
}

/**
 * Remix mode metadata (no draft)
 * User is remixing someone else's post, creates published post immediately
 */
export interface RemixModeMetadata extends BaseModeMetadata {
  mode: PostMode.REMIX;
  sourcePostId: string;               // Required: Source post ID
  targetPostId: null;                 // No target (creates new)
  isDraft: false;
}

/**
 * Remix draft mode metadata
 * User is remixing with draft support
 */
export interface RemixDraftModeMetadata extends BaseModeMetadata {
  mode: PostMode.REMIX_DRAFT;
  sourcePostId: string;               // Required: Source post ID
  targetPostId: string;               // Required: Draft ID
  isDraft: true;
}

/**
 * New post mode metadata
 * User is creating new post from scratch
 */
export interface NewModeMetadata extends BaseModeMetadata {
  mode: PostMode.NEW;
  sourcePostId: null;
  sourcePostData: null;
  targetPostId: null;
  isDraft: false;
}

/**
 * Discriminated union of all mode metadata types
 * TypeScript will narrow based on the `mode` field
 */
export type ModeMetadata = 
  | EditModeMetadata 
  | RemixModeMetadata 
  | RemixDraftModeMetadata 
  | NewModeMetadata;

/**
 * Type guard to check if metadata is for edit mode
 */
export function isEditMode(metadata: ModeMetadata): metadata is EditModeMetadata {
  return metadata.mode === PostMode.EDIT;
}

/**
 * Type guard to check if metadata is for remix mode
 */
export function isRemixMode(metadata: ModeMetadata): metadata is RemixModeMetadata {
  return metadata.mode === PostMode.REMIX;
}

/**
 * Type guard to check if metadata is for remix draft mode
 */
export function isRemixDraftMode(metadata: ModeMetadata): metadata is RemixDraftModeMetadata {
  return metadata.mode === PostMode.REMIX_DRAFT;
}

/**
 * Type guard to check if metadata is for new post mode
 */
export function isNewMode(metadata: ModeMetadata): metadata is NewModeMetadata {
  return metadata.mode === PostMode.NEW;
}

/**
 * Helper to get target post ID (works for edit and remix draft)
 */
export function getTargetPostId(metadata: ModeMetadata): string | null {
  if (isEditMode(metadata) || isRemixDraftMode(metadata)) {
    return metadata.targetPostId;
  }
  return null;
}

/**
 * Helper to check if mode supports drafts
 */
export function supportsDrafts(metadata: ModeMetadata): boolean {
  return isRemixDraftMode(metadata) || (isEditMode(metadata) && metadata.isDraft);
}

// Backward compatibility: Re-export as ItineraryMode for existing code
export const ItineraryMode = PostMode;
export type ItineraryModeType = PostModeType;

